import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import FishPenLine from '@/assets/icons/fish_pen_line.svg?component';
import AddLine from '@/assets/icons/ri_add_line.svg?component';
import { Button } from '@/components/atoms/Buttons';
import { Card } from '@/components/atoms/Card';

import { dayWidthPx } from '../../helpers/laneSpacing';
import { type BlockSortKey, sortLineageBlocks } from '../../helpers/lineageBlocks';
import { lineageOf, lineageVisibility } from '../../helpers/lineageVisibility';
import {
    type FocusSource,
    groupEmphasis,
    type LineEmphasis,
    transferEmphasis,
} from '../../helpers/lineFocus';
import { TRACK_MIN_WIDTH_PX } from '../../helpers/trackLabels';
import { trackStyle, weekCountStyle } from '../../helpers/trackStyle';
import { transferSegments } from '../../helpers/transferLines';
import type { LineageNotice, OriginOutcome } from '../../hooks/useBoardModel';
import { useTimelineViewport } from '../../hooks/useTimelineViewport';
import type { EmptyPen, GroupRowModel, LineageBlock, WeekAxis } from '../../types';
import AxisHeader from '../AxisHeader/AxisHeader';
import ColumnHeader, { type BlockSort } from '../ColumnHeader/ColumnHeader';
import Glyph from '../Glyph/Glyph';
import GroupLabel from '../GroupLabel/GroupLabel';
import GroupTrack from '../GroupTrack/GroupTrack';
import TimelineZoomBar from '../TimelineZoomBar/TimelineZoomBar';
import TransferLines from '../TransferLines/TransferLines';

import styles from './TimelineBoard.module.scss';

interface TimelineBoardProps {
    axis: WeekAxis;
    blocks: LineageBlock[];
    /** Null when today is off the axis. */
    todayPct: number | null;
    expandedBlocks: ReadonlySet<string>;
    onToggleBlock: (blockKey: string) => void;
    /** Rows whose Origin hid their unshared earlier rows (in a block showing its sources). */
    collapsedRows?: ReadonlySet<number>;
    /** The row whose lineage is lit, the others dimmed. */
    highlightedRow?: number | null;
    /** An Origin press; without it a press flips the whole block. */
    onToggleOrigin?: (blockKey: string, rowKey: number) => OriginOutcome | null;
    /**
     * The last lineage change to announce, from the expansion state; it also covers clears the
     * board doesn't see (a checkbox). Without it the board announces its own presses.
     */
    lineageNotice?: LineageNotice | null;
    emptyPens: EmptyPen[];
    onNewFishGroup: (cageId: number) => void;
    /** Opens a group's dialog. */
    onOpenGroup?: (groupId: number) => void;
    /** When set, the inline "+ New fish group" buttons are disabled and explain why. */
    createDisabledReason?: string | null;
    /** Id of a visible note explaining the disabled buttons. */
    createDescribedBy?: string;
    /** The scope shown; another one opens at its latest activity, a refetch keeps the view. */
    viewKey: string;
}

const NO_ROWS: ReadonlySet<number> = new Set();

/**
 * How many earlier rows stayed because others share them: for a collapsed row, and for the
 * lit row, whose press could hide none.
 */
function sharedCount(
    block: LineageBlock,
    rowKey: number,
    collapsedRows: ReadonlySet<number>,
    highlighted: boolean
): number {
    if (!collapsedRows.has(rowKey) && !highlighted) return 0;
    const collapsed = new Set([...collapsedRows, rowKey]);
    return lineageVisibility(block.parentRows, collapsed, block.liveRows).shared.get(rowKey) ?? 0;
}

/** The transfers that brought fish into a row from other rows, oldest first. */
function originsOf(block: LineageBlock, row: GroupRowModel) {
    const members = new Set(row.members.map((member) => member.groupId));
    return block.transfers
        .filter(
            (transfer) => members.has(transfer.destinationId) && !members.has(transfer.sourceId)
        )
        .sort((a, b) => a.date.getTime() - b.date.getTime());
}

/**
 * The week-axis Gantt: one row per fish group, or per chain of groups continuing each other in
 * the same pen, in lineage blocks with sources above results.
 */
export default function TimelineBoard(props: TimelineBoardProps) {
    const { axis, blocks, todayPct, expandedBlocks, onToggleBlock, emptyPens, onNewFishGroup } =
        props;
    const { createDisabledReason = null, createDescribedBy, onOpenGroup, viewKey } = props;
    const { collapsedRows = NO_ROWS, highlightedRow = null, onToggleOrigin, lineageNotice } = props;
    const { t } = useTranslation('fishGroups');
    // A line under the pointer (or its end under focus) wins over a hovered or focused group.
    // Either only changes which lines and bars are lit: the rows aren't laid out again.
    // As for groups, the line under the pointer and the one whose end holds keyboard focus are
    // kept apart; the pointer's wins while both are set.
    const [hoveredTransfer, setHoveredTransfer] = useState<string | null>(null);
    const [focusedTransfer, setFocusedTransfer] = useState<string | null>(null);
    const activeTransfer = hoveredTransfer ?? focusedTransfer;
    const focusTransfer = useCallback((source: FocusSource, key: string | null) => {
        if (source === 'pointer') setHoveredTransfer(key);
        else setFocusedTransfer(key);
    }, []);
    // The group under the pointer, and the one holding keyboard focus, are kept apart: leaving
    // with one doesn't drop the other. The pointer's wins while both are set.
    const [hoveredGroup, setHoveredGroup] = useState<number | null>(null);
    const [focusedGroup, setFocusedGroup] = useState<number | null>(null);
    const focusGroup = useCallback((source: FocusSource, groupId: number | null) => {
        if (source === 'pointer') setHoveredGroup(groupId);
        else setFocusedGroup(groupId);
    }, []);
    const allTransfers = useMemo(() => blocks.flatMap((block) => block.transfers), [blocks]);
    const emphasis = useMemo((): LineEmphasis | null => {
        const line = allTransfers.find((candidate) => candidate.key === activeTransfer);
        if (line) return transferEmphasis(line);
        const group = hoveredGroup ?? focusedGroup;
        return group === null ? null : groupEmphasis(allTransfers, group);
    }, [allTransfers, activeTransfer, hoveredGroup, focusedGroup]);
    const [announcement, setAnnouncement] = useState('');
    const litRows = useMemo(() => {
        const block = blocks.find((candidate) =>
            candidate.rows.some((row) => row.groupId === highlightedRow)
        );
        return block && highlightedRow !== null
            ? lineageOf(block.parentRows, highlightedRow)
            : null;
    }, [blocks, highlightedRow]);
    const pressOrigin = (block: LineageBlock, row: GroupRowModel) => {
        let outcome: OriginOutcome | null = 'shown';
        if (onToggleOrigin) outcome = onToggleOrigin(block.key, row.groupId);
        else onToggleBlock(block.key);
        if (outcome) setAnnouncement(t(`board.lineage.${outcome}`, { name: row.name }));
    };
    const announced =
        lineageNotice === undefined
            ? announcement
            : lineageNotice
              ? t(`board.lineage.${lineageNotice.kind}`, {
                    name: lineageNotice.name,
                    previous: lineageNotice.previous,
                })
              : '';
    const [sort, setSort] = useState<BlockSort>({ key: 'pen', direction: 'asc' });
    const sortedBlocks = useMemo(
        () => sortLineageBlocks(blocks, sort.key, sort.direction),
        [blocks, sort]
    );
    // Which rows each block shows, and their pieces of the transfer lines.
    const shownBlocks = useMemo(
        () =>
            sortedBlocks.map((block) => {
                const expanded = !block.hasSources || expandedBlocks.has(block.key);
                const hidden = expanded
                    ? lineageVisibility(block.parentRows, collapsedRows, block.liveRows).hidden
                    : null;
                // A row ending in a live group is never history, whichever way the block's
                // earlier groups are hidden.
                const rows = block.rows.filter(
                    (row) =>
                        row.isLive ||
                        ((expanded || row.role !== 'source') && !hidden?.has(row.groupId))
                );
                return { block, expanded, rows, segments: transferSegments(rows, block.transfers) };
            }),
        [sortedBlocks, expandedBlocks, collapsedRows]
    );
    // Labels and ticks are laid out again at the track's real width, so one hidden on a narrow
    // board shows once the weeks are wide enough; null until measured (and in jsdom).
    const viewport = useTimelineViewport(axis, todayPct, viewKey);
    const { trackPx, zoom } = viewport;
    const dayPx = dayWidthPx(trackPx ?? TRACK_MIN_WIDTH_PX, axis.startMs, axis.endMs);
    const compactLabels = zoom === 'month' || zoom === 'cycle';
    // While the track is wider than the board, the scroller takes focus so arrow keys scroll it.
    const regionProps = viewport.overflows
        ? { tabIndex: 0, role: 'region', 'aria-label': t('board.timelineRegion') }
        : {};
    const onSort = (key: BlockSortKey) =>
        setSort((current) =>
            current.key === key
                ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
                : { key, direction: 'asc' }
        );

    return (
        <Card padding="none" className={styles.card}>
            <p className={styles.announcer} role="status">
                {announced}
            </p>
            {zoom !== null && (
                <TimelineZoomBar
                    zoom={zoom}
                    onZoomChange={viewport.setZoom}
                    onToday={todayPct === null ? null : viewport.scrollToToday}
                />
            )}
            <div
                ref={viewport.scrollerRef}
                className={classNames(styles.scroller, zoom && styles[`zoom--${zoom}`])}
                style={{
                    ...weekCountStyle(axis.weeks.length, viewport.futureWeeks),
                    paddingInlineEnd: viewport.endPadPx,
                }}
                onScroll={viewport.onScroll}
                onKeyDown={viewport.onKeyDown}
                {...regionProps}>
                <div className={styles.header}>
                    <div ref={viewport.labelRef} className={styles.groupHeader}>
                        <ColumnHeader sort={sort} onSort={onSort} />
                    </div>
                    <AxisHeader
                        axis={axis}
                        futureWeeks={viewport.futureWeeks}
                        trackPx={viewport.drawnTrackPx}
                        ref={viewport.axisRef}
                        scroller={viewport.scroller}
                    />
                </div>

                <div className={styles.body}>
                    {todayPct !== null && (
                        <div className={styles.trackLayer} aria-hidden="true">
                            <span className={styles.today} style={trackStyle(todayPct)}>
                                <span className={styles.todayLabel}>
                                    {t('board.markers.today')}
                                </span>
                            </span>
                        </div>
                    )}

                    {shownBlocks.map(({ block, expanded, rows, segments }) => {
                        return (
                            <section key={block.key} className={styles.block}>
                                {rows.map((row) => (
                                    <div
                                        key={row.groupId}
                                        className={classNames(styles.row, {
                                            [styles.rowCompact]: row.role !== 'standalone',
                                            [styles.rowDimmed]:
                                                litRows !== null && !litRows.has(row.groupId),
                                            [styles.rowLit]: highlightedRow === row.groupId,
                                        })}>
                                        <TransferLines
                                            segments={segments.get(row.groupId)}
                                            activeKeys={emphasis?.lineKeys ?? null}
                                            onActiveChange={focusTransfer}
                                            dayPx={dayPx}
                                        />
                                        <GroupLabel
                                            row={row}
                                            sourcesShown={
                                                expanded && !collapsedRows.has(row.groupId)
                                            }
                                            origins={originsOf(block, row)}
                                            sharedCount={sharedCount(
                                                block,
                                                row.groupId,
                                                collapsedRows,
                                                highlightedRow === row.groupId
                                            )}
                                            highlighted={highlightedRow === row.groupId}
                                            onFocusChange={(source, focused) =>
                                                focusGroup(source, focused ? row.groupId : null)
                                            }
                                            onToggleOrigin={
                                                block.hasSources || row.originCount > 0
                                                    ? () => pressOrigin(block, row)
                                                    : undefined
                                            }
                                            onOpen={
                                                onOpenGroup
                                                    ? () => onOpenGroup(row.groupId)
                                                    : undefined
                                            }
                                            onOpenMember={onOpenGroup}
                                        />
                                        <GroupTrack
                                            row={row}
                                            trackPx={trackPx}
                                            compactLabels={compactLabels}
                                            viewPx={viewport.viewPx}
                                            showCreationBadge={!expanded}
                                            emphasisedGroups={emphasis?.groups}
                                            onGroupFocus={focusGroup}
                                            onOpen={onOpenGroup}
                                        />
                                    </div>
                                ))}
                            </section>
                        );
                    })}

                    {emptyPens.map((pen) => (
                        <div key={pen.cageId} className={classNames(styles.row, styles.emptyRow)}>
                            <div className={styles.emptyLabel}>
                                <span className={styles.emptyPen}>
                                    <Glyph icon={FishPenLine} />
                                    {t('board.row.emptyPen', { pen: pen.displayName })}
                                </span>
                                <Button
                                    variant="secondary"
                                    size="small"
                                    icon={<Glyph icon={AddLine} />}
                                    disabled={createDisabledReason !== null}
                                    tooltip={createDisabledReason ?? undefined}
                                    aria-describedby={createDescribedBy}
                                    onClick={() => onNewFishGroup(pen.cageId)}>
                                    {t('board.actions.newFishGroup')}
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </Card>
    );
}
